import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import IconButton from 'LuxKit/Button/IconButton'
import clsx from 'clsx'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from '../Typography/BodyText'
import type { ImageCarouselRefInterface } from './BaseImageCarousel'
import BaseImageCarousel from './BaseImageCarousel'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getThemeClassName } from 'lib/theme/themeUtils'
import useTextDirection from 'hooks/useTextDirection'
import type { PhysicalHorizontalDirection } from './utils'
import { getLogicalDirectionForPhysicalHorizontalDirection } from './utils'
import useCarouselArrowKind from './useCarouselArrowKind'
import { useScreenSizeUp } from 'hooks/useScreenSize'

const NavigationArrow = styled(IconButton)`
  display: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  inset-inline-start: 12px;
  z-index: 1;
  transition: color 0.2s, background-color 0.2s, border-color 0.2s, opacity 0.2s;

  &.right {
    transform: translateY(-50%);
    inset-inline-start: auto;
    inset-inline-end: 12px;
  }

  ${mediaQueryUp.tablet} {
    display: inline-flex;
  }
`

const Container = styled.div`
  position: relative;
  height: 100%;
  width: 100%;

  &.arrow-interaction-visible {
    ${NavigationArrow} {
      opacity: 0;
      pointer-events: none;
    }

    &:hover, &:has(:focus-visible) {
      ${NavigationArrow} {
        opacity: 0.6;
        pointer-events: auto;

        &:hover, &:focus-visible {
          opacity: 1;
        }
      }
    }
  }

  &.arrow-hidden {
    ${NavigationArrow} {
      display: none;
    }
  }

`

const CarouselOverlay = styled.div`
  position: absolute;
  inset: 0;
  height: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  padding: ${rem(12)};

  pointer-events: none;

  display: grid;
  gap: ${rem(8)};
  grid-template-areas:
    "top-left top-right"
    "bottom-left bottom-right";

  > * {
    max-width: 100%;
    overflow: hidden;
  }

  > .top-left {
    justify-self: flex-start;
    align-self: flex-start;
    grid-area: top-left;
  }

  > .top-right {
    justify-self: flex-end;
    align-self: flex-start;
    grid-area: top-right;
  }

  > .bottom-left {
    justify-self: flex-start;
    align-self: flex-end;
    grid-area: bottom-left;
  }

  > .bottom-right {
    justify-self: flex-end;
    align-self: flex-end;
    grid-area: bottom-right;
  }

  a, button {
    pointer-events: auto;
  }
`

const OverlayWrapper = styled.div`
  padding: ${rem(4)} ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground}; /* Fallback for ancient browsers */
  background-color: color-mix(in srgb, ${props => props.theme.palette.neutral.surface.darkBackground}, transparent 20%);
`

const CrossfadePreview = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s;

  &.active {
    opacity: 1;
  }
`

interface Props extends React.ComponentProps<typeof BaseImageCarousel> {
  /**
   * How the arrows that navigate the images (next/previous) should behave.
   * Interaction-visible means they only become visible when a user interactions with the carousel via focus or mouse hover
   **/
  arrowBehaviour?: 'always-visible' | 'interaction-visible' | 'hidden'
  /** Whether or not to add the image titles in the bottom left hand corner along with the counter */
  showImageTitles?: boolean
  /** Whether to show the current image position when no bottom-left content is provided. */
  showImageCounter?: boolean
  /** 'slot' in the top left corner of carousel to place content, e.g. offer type labels */
  topLeft?: React.ReactNode
  /** 'slot' in the top right corner of carousel to place content, e.g. offer badges */
  topRight?: React.ReactNode
  /**
   * 'slot' in the bottom left corner of carousel to place content, e.g. offer type labels
   * If not specified will show an image counter
   * */
  bottomLeft?: React.ReactNode
  /** 'slot' in the bottom right corner of carousel to place content, e.g. offer type labels */
  bottomRight?: React.ReactNode

  /** How fast to auto-scroll */
  autoScrollSpeed?: number

  /** Style of the arrow buttons */
  arrowStyle?: 'dark' | 'ghost'

  /** Whether carousel arrow icons should mirror in RTL. */
  arrowIconRtlFlip?: boolean

  /** How to preview the second image while a desktop user hovers the carousel. */
  hoverPreviewBehaviour?: 'slide' | 'crossfade'

  /**
   * Controls the hover preview from an ancestor interaction surface.
   * Use this when another element, such as a full-card click target, sits above
   * the carousel and therefore owns the pointer events.
   */
  isHoverPreviewActive?: boolean
}

function ImageCarousel(props: Props) {
  const {
    images,
    className,
    carouselRef,
    onImageChange,
    onImageLoad,
    arrowBehaviour = 'interaction-visible',
    interstitials = EmptyArray,
    bottomLeft,
    bottomRight,
    topLeft,
    topRight,
    showImageTitles,
    showImageCounter = true,
    defaultIndex = 0,
    autoScrollSpeed,
    arrowStyle = 'dark',
    arrowIconRtlFlip = false,
    hoverPreviewBehaviour,
    isHoverPreviewActive,
    ...baseCarouselProps
  } = props

  const [currentImage, setCurrentImage] = useState<{ index: number, image?: App.Image }>({ index: defaultIndex })
  const localCarouselRef = useRef<ImageCarouselRefInterface | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const textDirection = useTextDirection(containerRef)
  const arrowKind = useCarouselArrowKind()
  const isDesktop = useScreenSizeUp('tablet')
  const isHoverPreviewEnabled = !!hoverPreviewBehaviour && isDesktop
  const isInSlideHoverPreviewRef = useRef(false)
  const [isCrossfadePreviewActive, setIsCrossfadePreviewActive] = useState(false)

  useImperativeHandle(carouselRef, () => localCarouselRef.current!)

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const restartAutoScroll = useCallback(() => {
    if (!autoScrollSpeed) return
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      localCarouselRef.current?.nextImage()
    }, autoScrollSpeed)
  }, [autoScrollSpeed])

  useEffect(() => {
    restartAutoScroll()
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [autoScrollSpeed, restartAutoScroll])

  const onArrowClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    restartAutoScroll()
    const direction = e.currentTarget.dataset.direction as PhysicalHorizontalDirection
    const logicalDirection = getLogicalDirectionForPhysicalHorizontalDirection(textDirection, direction)
    if (logicalDirection === 'previous') {
      localCarouselRef.current?.previousImage()
    } else {
      localCarouselRef.current?.nextImage()
    }
  }, [restartAutoScroll, textDirection])

  const imageItemCount = images.length + interstitials.length

  const bottomLeftText = useMemo(() => {
    const imageAltText = currentImage.image?.title

    const text: Array<string> = []
    if (showImageCounter && imageItemCount > 1) {
      text.push(`${currentImage.index + 1} / ${imageItemCount}`)
    }
    if (showImageTitles && imageAltText) {
      text.push(imageAltText)
    }

    return text.join(' - ')
  }, [currentImage, imageItemCount, showImageCounter, showImageTitles])

  const updateCurrentImage = useCallback((index: number, image?: App.Image) => {
    if (isInSlideHoverPreviewRef.current && index !== 1) {
      isInSlideHoverPreviewRef.current = false
    }
    if (index !== 0) setIsCrossfadePreviewActive(false)
    setCurrentImage({ index, image })
    onImageChange?.(index, image)
    restartAutoScroll()
  }, [onImageChange, restartAutoScroll])

  const startHoverPreview = useCallback(() => {
    if (
      isHoverPreviewEnabled &&
      images.length > 1 &&
      localCarouselRef.current?.getCurrentImageIndex() === 0
    ) {
      if (hoverPreviewBehaviour === 'slide') {
        isInSlideHoverPreviewRef.current = true
        localCarouselRef.current.goToImage(images[1], 'smooth')
      } else if (hoverPreviewBehaviour === 'crossfade') {
        setIsCrossfadePreviewActive(true)
      }
    }
  }, [hoverPreviewBehaviour, images, isHoverPreviewEnabled])

  const endHoverPreview = useCallback(() => {
    if (isHoverPreviewEnabled && isInSlideHoverPreviewRef.current) {
      localCarouselRef.current?.goToImage(images[0], 'smooth')
    }
    isInSlideHoverPreviewRef.current = false
    setIsCrossfadePreviewActive(false)
  }, [images, isHoverPreviewEnabled])

  useEffect(() => {
    if (isHoverPreviewActive === undefined) return

    if (isHoverPreviewActive) {
      startHoverPreview()
    } else {
      endHoverPreview()
    }
  }, [endHoverPreview, isHoverPreviewActive, startHoverPreview])

  return (<Container
    ref={containerRef}
    className={clsx(getThemeClassName({ mobile: 'default' }), className, `arrow-${arrowBehaviour}`)}
    onMouseEnter={isHoverPreviewActive === undefined ? startHoverPreview : undefined}
    onMouseLeave={isHoverPreviewActive === undefined ? endHoverPreview : undefined}
  >
    <CarouselOverlay>
      {!!topLeft && (
        <div className="top-left">
          {topLeft}
        </div>
      )}
      {!!topRight && <div className="top-right">{topRight}</div>}
      <div className="bottom-left">
        {bottomLeft}
        {!bottomLeft && !!bottomLeftText && <OverlayWrapper>
          <BodyText variant="small" colour="dark-contrast" wrap="truncate" title={bottomLeftText}>{bottomLeftText}</BodyText>
        </OverlayWrapper>}
      </div>
      {!!bottomRight && <div className="bottom-right">{bottomRight}</div>}
    </CarouselOverlay>
    {imageItemCount > 1 &&
      <NavigationArrow
        onClick={onArrowClick}
        kind={arrowKind}
        variant={arrowStyle}
        shape="circle"
        data-direction="left"
      >
        <LineAngleLeftIcon rtlFlip={arrowIconRtlFlip} />
      </NavigationArrow>}
    <BaseImageCarousel
      {...baseCarouselProps}
      images={images}
      carouselRef={localCarouselRef}
      onImageChange={updateCurrentImage}
      onImageLoad={onImageLoad}
      interstitials={interstitials}
      defaultIndex={defaultIndex}
    />
    {isHoverPreviewEnabled && hoverPreviewBehaviour === 'crossfade' && images.length > 1 &&
      <CrossfadePreview className={clsx('crossfade-hover-preview', { active: isHoverPreviewEnabled && isCrossfadePreviewActive })}>
        <BaseImageCarousel
          images={[images[1]]}
          imageParams={baseCarouselProps.imageParams}
          disablePlaceholders={baseCarouselProps.disablePlaceholders}
        />
      </CrossfadePreview>}
    {imageItemCount > 1 &&
      <NavigationArrow
        onClick={onArrowClick}
        className="right"
        variant={arrowStyle}
        kind={arrowKind}
        shape="circle"
        data-direction="right"
      >
        <LineAngleRightIcon rtlFlip={arrowIconRtlFlip} />
      </NavigationArrow>}
  </Container>)
}

export default React.memo(ImageCarousel)
